import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {vec3} from 'gl-matrix';

const tempVec3 = vec3.create();

interface Effect {
    start: number;
    duration: number;
    update: (time: number) => void;
    onComplete: () => void;
}

export function move(o: Object3D, from: vec3, to: vec3) {
    return (f: number) => o.setPositionLocal(vec3.lerp(tempVec3, from, to, f));
}

export function rotateLocal(o: Object3D, axis: vec3, from: number, to: number) {
    return (f: number) => {
        o.resetRotation();
        o.rotateAxisAngleDegLocal(axis, from * f + to * (1 - f));
    };
}

export function scale(o: Object3D, from: vec3, to: vec3) {
    return (f: number) => o.setScalingLocal(vec3.lerp(tempVec3, from, to, f));
}

export function uniformScale(o: Object3D, from: number, to: number) {
    return (f: number) => {
        const s = f * to + (1 - f) * from;
        vec3.set(tempVec3, s, s, s);
        o.setScalingLocal(tempVec3);
    };
}

export async function delay(duration: number) {
    return new Promise((res, rej) => {
        setTimeout(res, duration * 1000);
    });
}

export type VFXPlayer = (update: (time: number) => void, duration: number) => Promise<void>;

/**
 * fruit-power-vfx-manager
 */
export class FruitPowerVfxManager extends Component {
    static TypeName = 'fruit-power-vfx-manager';

    /* Properties that are configurable in the editor */

    @property.object()
    theObj!: Object3D;

    effects: Effect[] = [];

    time = 0;

    boltObject!: Object3D;
    abyssObject!: Object3D;
    spikeObject!: Object3D;
    fireObject!: Object3D;
    sandObject!: Object3D;
    sandParticle!: Object3D;

    start() {
        this.boltObject = this.object.findByNameDirect('Bolt')[0];
        this.boltObject.active = false;

        this.abyssObject = this.object.findByNameDirect('Abyss')[0];
        this.abyssObject.active = false;

        this.spikeObject = this.object.findByNameDirect('Spike')[0];
        this.spikeObject.active = false;

        this.fireObject = this.object.findByNameDirect('Fire')[0];
        this.fireObject.active = false;

        this.sandObject = this.object.findByNameDirect('Sand')[0];

        this.sandParticle = this.object.findByNameDirect('SandParticle')[0];
        this.sandParticle.active = false;
    }

    update(dt: number) {
        this.time += dt;

        for (let i = this.effects.length - 1; i >= 0; --i) {
            const e = this.effects[i];
            const f = Math.min((this.time - e.start) / e.duration, 1);
            e.update(f);
            if (f >= 1.0) {
                e.onComplete();
                this.effects.splice(i, 1);
            }
        }
    }

    play(update: (time: number) => void, duration: number): Promise<void> {
        return new Promise((res, _) => {
            this.effects.push({
                start: this.time,
                duration,
                update,
                onComplete: res,
            });
        });
    }
}
